import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumb } from "@/components/Breadcrumb";
import { StructuredData } from "@/components/StructuredData";
import { DiceRoller } from "@/components/DiceRoller";
import { CoinFlip } from "@/components/CoinFlip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shuffle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import gamesImage from "@/assets/games-hero.jpg";

const CardShuffler = () => {
  const suits = ["♠", "♥", "♦", "♣"];
  const values = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
  const [drawnCards, setDrawnCards] = useState<string[]>([]);

  const drawCard = () => {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    const suit = suits[array[0] % suits.length];
    const value = values[array[1] % values.length];
    const card = `${value}${suit}`;
    setDrawnCards([card, ...drawnCards.slice(0, 4)]);
    toast.success(`Drew ${card}`);
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Shuffle className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold">Card Shuffler</h2>
      </div>

      <Button onClick={drawCard} className="w-full mb-4">
        Draw Random Card
      </Button>

      {drawnCards.length > 0 && (
        <div className="grid grid-cols-5 gap-2">
          {drawnCards.map((card, index) => (
            <div
              key={index}
              className="aspect-[2/3] bg-card border-2 border-border rounded-lg flex items-center justify-center text-3xl font-bold"
              style={{
                color: card.includes("♥") || card.includes("♦") ? "#ef4444" : "#000",
              }}
            >
              {card}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const Games = () => {
  const breadcrumbs = [
    { label: "Games", href: "/games" }
  ];

  return (
    <Layout>
      <SEO
        title="Random Games - Dice Roller, Coin Flip & Card Shuffler"
        description="Play random games with cryptographically secure randomness. Roll dice, flip coins, shuffle cards, and more. Free online random game tools."
        canonical="https://numbers-generator.com/games"
        keywords="dice roller, coin flip, card shuffler, random games, online dice, virtual coin toss"
      />
      <StructuredData 
        type="BreadcrumbList"
        breadcrumbs={[
          { name: "Home", url: "https://numbers-generator.com/" },
          { name: "Games", url: "https://numbers-generator.com/games" }
        ]}
      />
      <div className="container mx-auto px-4 py-12">
        <Breadcrumb items={breadcrumbs} />
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Games - Dice, Coins & Cards
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Dice rollers, coin flips, and card games with true randomness
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto mb-12">
          <DiceRoller />
          <CoinFlip />
          <div className="lg:col-span-2">
            <CardShuffler />
          </div>
        </div>

        {/* Games Hero Image at Bottom */}
        <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={gamesImage} 
            alt="Casino gaming table with dice, playing cards, and poker chips - cryptographically secure random number generation for dice roller, coin flip, and card shuffler games"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Games;
